import { CobaltAvatar } from '@cae/cobalt-react/components/avatar';
import { CobaltCheckbox } from '@cae/cobalt-react/components/checkbox';
import {
  CobaltClickableTile,
  CobaltTile,
} from '@cae/cobalt-react/components/tile';
import { useInstructorNavigation } from '@features/instructors/hooks/use-instructor-navigation/useInstructorNavigation';
import * as React from 'react';

import type { InstructorsTileProps } from './InstructorsTile.types';

import styles from './InstructorsTile.module.css';

const InstructorsTile = React.memo(
  ({
    instructor,
    selectable = false,
    selected,
    shouldRedirectToDetailPanel = false,
    onSelectionChange,
    clickable = false,
    badge,
    programOverride,
  }: Readonly<InstructorsTileProps>): React.ReactElement => {
    const isSelected = selected ?? false;

    const [maxProgramsToShow, setMaxProgramsToShow] = React.useState<number>(5);
    const programDetailsRef = React.useRef<HTMLDivElement>(null);
    const avatarRef = React.useRef<HTMLDivElement>(null);
    const tileRef = React.useRef<HTMLDivElement>(null);
    const { navigateToInstructor } = useInstructorNavigation();
    const TileComponent = clickable ? CobaltClickableTile : CobaltTile;

    React.useLayoutEffect(() => {
      if (
        programDetailsRef.current &&
        tileRef.current &&
        avatarRef.current &&
        maxProgramsToShow > 0
      ) {
        const programDetailsWidth = programDetailsRef.current.clientWidth;
        const tileWidth = tileRef.current.clientWidth;
        const avatarWidth = avatarRef.current.clientWidth;
        // Multiplying avatar Width by 3 to account for tile's paddings/margins
        const isWrapping = programDetailsWidth > tileWidth - avatarWidth * 3;
        if (isWrapping && maxProgramsToShow > 1) {
          setMaxProgramsToShow(
            prevMaxProgramsToShow => prevMaxProgramsToShow - 1
          );
        }
      }
    }, [maxProgramsToShow]);

    const programIds = React.useMemo(
      () => instructor.programs.map(p => p.id),
      [instructor.programs]
    );
    const programsToDisplay = React.useMemo(
      () => programIds.slice(0, maxProgramsToShow),
      [programIds, maxProgramsToShow]
    );
    const extraProgramsLabel = React.useMemo(
      () =>
        programIds.length > maxProgramsToShow
          ? ` (+${programIds.length - maxProgramsToShow} programs)`
          : null,
      [programIds, maxProgramsToShow]
    );

    const handleSelectionToggle = React.useCallback(() => {
      onSelectionChange?.(instructor.instructorId, !isSelected);
    }, [instructor.instructorId, isSelected, onSelectionChange]);

    const handleTileClick = React.useCallback(() => {
      if (selectable) {
        handleSelectionToggle();
      }
      if (shouldRedirectToDetailPanel) {
        navigateToInstructor(instructor.instructorId);
      }
    }, [
      selectable,
      shouldRedirectToDetailPanel,
      navigateToInstructor,
      instructor.instructorId,
      handleSelectionToggle,
    ]);

    return (
      <TileComponent
        className={`${styles.instructorTile}${isSelected ? ` ${styles.selectedTile}` : ''}`}
        data-testid="instructors-tile"
        ref={tileRef}
        id={`instructor-tile-${instructor.instructorId}`}
        onClick={handleTileClick}
      >
        <div className={styles.instructorInfo}>
          <section ref={avatarRef}>
            <CobaltAvatar
              firstName={instructor.firstName}
              lastName={instructor.lastName}
              color="neutral"
            />
          </section>
          <section>
            <p
              className={`${styles.instructorName} body-02`}
              data-testid="instructor-name"
            >
              {instructor.displayName}
            </p>
            <p
              ref={programDetailsRef}
              className={`${styles.programDetails} body-01`}
              data-testid="program-details"
            >
              {instructor.primaryTrainingCenter.name}
              <span className={styles.pipe}>|</span>
              {programOverride ??
                programsToDisplay.join(', ') + (extraProgramsLabel ?? '')}
            </p>
          </section>
        </div>
        {selectable && (
          <section
            className={styles.checkbox}
            onClick={e => e.stopPropagation()}
          >
            <CobaltCheckbox
              hideLabel
              checked={isSelected}
              onChange={handleSelectionToggle}
              labelText={`Select ${instructor.displayName}`}
            />
          </section>
        )}
        {badge ? (
          <span className={styles.badge} data-testid="instructors-tile-badge">
            {badge}
          </span>
        ) : null}
      </TileComponent>
    );
  }
);

InstructorsTile.displayName = 'InstructorsTile';
export { InstructorsTile };
